import { ChevronDown, X } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "@repo/utils";
import { SelectInputText } from "./select-input-text.js";
import type { SelectOption, SelectOptionValue, Variant } from "./select.js";

type Props = {
  toggleOpen: () => void;
  isSelectOpen: boolean;
  hasError: boolean;
  selectedValues: string[];
  handleClearAll: (e: React.MouseEvent<HTMLButtonElement>) => void;
  selectedOptions: SelectOption<SelectOptionValue>[];
  multiple?: boolean;
  placeholder?: string;
  showClearIcon?: boolean;
  darkSelectedText?: boolean;
  variant?: Variant;
} & ComponentProps<"button">;

export const SelectInputButton = ({
  toggleOpen,
  isSelectOpen,
  hasError,
  showClearIcon,
  selectedValues,
  handleClearAll,
  selectedOptions,
  placeholder,
  multiple,
  darkSelectedText,
  variant = "default",
  ...props
}: Props) => {
  const { disabled } = props;
  return (
    <button
      {...props}
      type="button"
      onClick={() => !disabled && toggleOpen()}
      className={cn(
        "flex h-12 w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm transition-colors outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50",
        {
          "border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500": hasError,
          "border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500": !hasError,
          "border-blue-500 ring-2 ring-blue-500": isSelectOpen,
          "h-8 py-1 text-[12px]": variant === "small",
        }
      )}
    >
      <span className="min-w-0 flex-1 truncate text-left">
        <SelectInputText
          selectedOptions={selectedOptions}
          placeholder={placeholder}
          multiple={multiple}
          darkSelectedText={darkSelectedText}
        />
      </span>

      <div className="flex flex-shrink-0 items-center gap-1">
        {showClearIcon && selectedValues.length > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            className="rounded p-0.5 transition-colors hover:bg-gray-100"
          >
            <X className="h-3 w-3 text-gray-500" />
          </button>
        )}
        <ChevronDown
          className={cn("h-4 w-4 text-gray-400 transition-transform", {
            "rotate-180": isSelectOpen,
          })}
        />
      </div>
    </button>
  );
};
